import { useState } from 'react';
import { Link } from 'react-router-dom';

const NEWSLETTER_URL = 'https://readdy.ai/api/form/d72ma44ij602egd95l5g';
const LOGO_URL = 'https://public.readdy.ai/ai/img_res/fe6b96b6-a4b6-4cea-93e9-3d0dcbee93ab.png';

const quickLinks = [
  { label: 'Home', href: '#home', route: false },
  { label: 'Services', href: '#services', route: false },
  { label: 'Products', href: '#products', route: false },
  { label: 'About Us', href: '#about', route: false },
  { label: 'Contact', href: '#contact', route: false },
  { label: 'FAQ', href: '#faq', route: false },
  { label: 'Locations', href: '/locations', route: true },
];

const services = [
  'Mobile Tyre Fitting',
  'Puncture Repair',
  'Wheel Balancing',
  'TPMS Services',
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus('sending');
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch(NEWSLETTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubStatus(res.ok ? 'done' : 'error');
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <footer className="bg-[#0F2A1D] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Col 1 — Brand */}
          <div className="lg:col-span-1">
            <img src={LOGO_URL} alt="BritFit Mobile Tyres" className="h-12 w-auto object-contain mb-5" />
            <p className="text-stone-400 text-xs leading-relaxed mb-5">
              UK&apos;s trusted mobile tyre fitting service. We come to you — at home, work, or roadside — across England, Scotland &amp; Wales.
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-facebook-fill', href: '#' },
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-twitter-x-line', href: '#' },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-[#1B4332] rounded-lg transition-colors cursor-pointer text-white"
                  aria-label={s.icon}
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Col 2 — Quick Links */}
          <div>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-5">Quick Links</h4>
            <ul className="flex flex-col gap-2.5">
              {quickLinks.map((l) => (
                <li key={l.href}>
                  {l.route ? (
                    <Link to={l.href} className="text-stone-300 hover:text-white text-sm transition-colors cursor-pointer">
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      href={l.href}
                      onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
                      className="text-stone-300 hover:text-white text-sm transition-colors cursor-pointer"
                    >
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Services */}
          <div>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-5">Services</h4>
            <ul className="flex flex-col gap-2.5">
              {services.map((s) => (
                <li key={s}>
                  <a
                    href="#services"
                    onClick={(e) => { e.preventDefault(); scrollTo('#services'); }}
                    className="text-stone-300 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    {s}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-4">Contact</h4>
              <div className="flex flex-col gap-2">
                <a href="tel:+441234567890" className="flex items-center gap-2 text-stone-300 hover:text-white text-sm cursor-pointer transition-colors">
                  <i className="ri-phone-line text-[#6EE7B7]" /> +44 1234 567890
                </a>
                <a href="mailto:hello@britfit.co.uk" className="flex items-center gap-2 text-stone-300 hover:text-white text-sm cursor-pointer transition-colors">
                  <i className="ri-mail-line text-[#6EE7B7]" /> hello@britfit.co.uk
                </a>
              </div>
            </div>
          </div>

          {/* Col 4 — Newsletter */}
          <div>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-5">Stay Updated</h4>
            <p className="text-stone-400 text-xs mb-4 leading-relaxed">
              Get tyre tips, seasonal advice, and exclusive offers direct to your inbox.
            </p>
            {subStatus === 'done' ? (
              <div className="bg-[#1B4332]/50 text-green-300 text-xs rounded-xl px-4 py-3">
                Thanks for subscribing! Check your inbox soon.
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleNewsletter}
                className="flex flex-col gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Your email address"
                  className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-[#6EE7B7] transition-colors"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'sending'}
                  className="w-full bg-[#1B4332] hover:bg-[#256344] text-white text-sm font-semibold py-2.5 rounded-xl cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60"
                >
                  {subStatus === 'sending' ? 'Subscribing...' : 'Subscribe'}
                </button>
                {subStatus === 'error' && (
                  <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                )}
              </form>
            )}
            <div className="flex flex-wrap gap-3 mt-5">
              {['Michelin Partner', 'Pirelli Approved', 'ATS Member'].map((b) => (
                <span key={b} className="text-[10px] text-stone-500 border border-white/10 px-2.5 py-1 rounded-full">{b}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 py-5 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500">
          <span>&copy; {new Date().getFullYear()} BritFit Mobile Tyres Ltd. All rights reserved.</span>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors cursor-pointer">Terms &amp; Conditions</a>
            <a href="#" className="hover:text-white transition-colors cursor-pointer">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
